import {Component, OnInit} from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexGrid,
  ApexLegend, ApexPlotOptions,
  ApexStroke,
  ApexTitleSubtitle,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
} from 'ng-apexcharts';
import {IntegrationsService} from '../../../shared/services/integrations.service';
import {Integration} from '../../../models/Integration';
import {Observable} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {UtilService} from '../../../shared/services/util.service';
import {AnalyticsService} from '../../../shared/services/analytics.service';
import {AnalyticsSales} from '../../../models/AnalyticsSales';

export type ChartOptions = {
  colors: string[];
  series: ApexAxisChartSeries;
  chart: ApexChart;
  tooltip: ApexTooltip;
  xaxis: ApexXAxis;
  stroke: ApexStroke;
  dataLabels: ApexDataLabels;
  yaxis: ApexYAxis;
  plotOptions: ApexPlotOptions;
  title: ApexTitleSubtitle;
  labels: string[];
  grid: ApexGrid;
  legend: ApexLegend;
  subtitle: ApexTitleSubtitle;
  fill: any;
};

@Component({
  selector: 'app-sales',
  templateUrl: './sales.component.html',
  styleUrls: ['./sales.component.scss']
})
export class AnalyticsSalesComponent implements OnInit {
  salesChartOptions: Partial<ChartOptions>;
  sourceChartOptions: Partial<ChartOptions>;

  fastFilters = [
    { id: 'today', text: 'Hoje' },
    { id: 'yesterday', text: 'Ontem' },
    { id: '7days', text: '7 dias' },
    { id: '15days', text: '15 dias' },
    { id: 'actualMonth', text: 'Mês atual' },
    { id: 'lastMonth', text: 'Mês anterior' },
  ];

  integrations$: Observable<Integration[]> = this.integrationService.listAnalytics();

  fastFilter = null;
  integrationId = '0';
  startDate: any;
  endDate: any;
  salesIndicator: AnalyticsSales;
  loading = false;

  constructor(
    private analyticsService: AnalyticsService,
    private integrationService: IntegrationsService,
    private route: ActivatedRoute,
    private router: Router,
    private utilService: UtilService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.integrationId = params.integration_id;

      if (params.start_date || params.end_date) {
        this.setStartDate(params.start_date);
        this.setEndDate(params.end_date);
      } else {
        this.applyFastFilter('7days');
      }

      this.doFilter();
    });
  }

  setStartDate(date: string): void  {
    if (date) {
      this.startDate = this.utilService.isoToDate(date);
    }
  }

  setEndDate(date: string): void  {
    if (date) {
      this.endDate = this.utilService.isoToDate(date);
    }
  }

  doFilter(): void {
    const startDate = this.utilService.dateToIso(this.startDate);
    const endDate = this.utilService.dateToIso(this.endDate);

    this.loading = true;
    this.analyticsService.getSalesIndicator(
      this.integrationId,
      startDate,
      endDate
    ).subscribe({
      next: salesIndicator => {
        this.loading = false;
        this.salesIndicator = salesIndicator;
        this.showSalesChartOptions();
        this.showSourceChartOptions();
      },
      error: () => { this.loading = false; },
    });
  }

  showSalesChartOptions(): void {
    this.salesChartOptions = {
      colors: ['#00BE9A'],
      series: [{
        data: this.salesIndicator.sales.map(sale => sale.amount)
      }],
      chart: {
        type: 'area',
        height: 350,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        foreColor: '#00BE9A',
      },
      dataLabels: {
        enabled: true,
      },
      grid: {
        show: false,
      },
      xaxis: {
        type: 'category',
        categories: this.salesIndicator.sales.map(sale => this.utilService.isoToBr(sale.payment_date)),
      },
      tooltip: {
        enabled: false,
      },
      labels: [],
      legend: {
        horizontalAlign: 'left',
      },
      fill: {
        opacity: 0.3,
        type: 'gradient',
        gradient: {
          shade: 'dark',
          opacityFrom: 0.7,
          opacityTo: 0.3,
        },
      },
    };
  }

  showSourceChartOptions(): void {
    this.sourceChartOptions = {
      colors: ['#67679B', '#00BE9A'],
      series: [{
        name: 'Qtd. Vendas',
        data: this.salesIndicator.sales_utm.map(sale => sale.quantity)
      }, {
        name: 'R$ Vendas',
        data: this.salesIndicator.sales_utm.map(sale => sale.amount)
      }],
      chart: {
        type: 'bar',
        height: 350,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        foreColor: '#FFFFFF',
      },
      plotOptions: {
        bar: {
          dataLabels: {
            position: 'top', // top, center, bottom
          },
        }
      },
      dataLabels: {
        enabled: true,
      },
      grid: {
        show: false,
      },
      xaxis: {
        categories: this.salesIndicator.sales_utm.map(sale => sale.utm)
      },
      tooltip: {
        enabled: false,
      },
      fill: {
        opacity: 0.3,
        type: 'gradient',
        gradient: {
          shade: 'dark',
          opacityFrom: 0.7,
          opacityTo: 0.3,
        },
      },
    };
  }

  applyFastFilter(filterId: string): void {
    if (this.fastFilter === filterId) {
      return;
    }

    this.fastFilter = filterId;
    const today = new Date();
    const startDate = new Date(today);
    const endDate = new Date(today);

    // Utilizando um function translator removemos todos os demais IFs/Else
    const filterFunctions: Record<string, () => void> = {
      yesterday: () => {
        startDate.setDate(today.getDate() - 1);
        endDate.setDate(today.getDate() - 1);
      },
      '7days': () => startDate.setDate(today.getDate() - 7),
      '15days': () => startDate.setDate(today.getDate() - 15),
      actualMonth: () => startDate.setDate(1),
      lastMonth: () => {
        startDate.setDate(1);
        startDate.setMonth(today.getMonth() - 1);
        endDate.setDate(0);
      }
    };

    filterFunctions[filterId]?.();

    this.startDate = startDate;
    this.endDate = endDate;

    this.applyFilter();
  }

  applyFilter(): void {
    let startDate = this.utilService.dateToIso(this.startDate);
    let endDate = this.utilService.dateToIso(this.endDate);
    if (!startDate && endDate) {
      startDate = endDate;
    }
    if (!endDate && startDate) {
      endDate = startDate;
    }
    this.setStartDate(startDate);
    this.setEndDate(endDate);
    this.router.navigate(['.'], {
      relativeTo: this.route,
      queryParams: {
        integration_id: this.integrationId !== '0' ? this.integrationId : null,
        start_date: startDate,
        end_date: endDate
      }
    });
  }

  clearFilter(): void {
    this.integrationId = '0';
    this.applyFastFilter('7days');
    this.applyFilter();
  }
}
